import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { comics } from "@/mocks/comics";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <Layout>
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-16 text-center">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">
            漫画未找到
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            抱歉，您要查看的漫画不存在或已被移除。
          </p>
          <Link
            to="/category/all"
            className="inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回漫画大全
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedComics = comics
    .filter(
      (c) => c.categorySlug === comic.categorySlug && c.id !== comic.id
    )
    .slice(0, 4);

  const pageTitle = `${comic.title} - 韩漫完整在线观看 - 韩漫漫画网`;
  const pageDesc = `${comic.title}是${comic.author}创作的${comic.category}类韩漫。${comic.description.slice(0, 80)}...韩漫漫画网提供完整在线观看，每日更新。`;
  const comicArticleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: pageTitle,
    description: pageDesc,
    author: {
      "@type": "Person",
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.date || "2025-01-01",
    dateModified: comic.updateDate,
    url: `https://www.hanmancomic.com/article/${comic.id}`,
    image: comic.cover,
  };

  return (
    <Layout>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title},${comic.author},${comic.category}韩漫,韩漫漫画网,韩漫在线观看`}
        canonicalUrl={`https://www.hanmancomic.com/article/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schemaJson={[comicArticleSchema]}
      />

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "漫画大全", href: "/category/all" },
            {
              label: comic.category,
              href: `/category/${comic.categorySlug}`,
            },
            { label: comic.title },
          ]}
        />

        <article className="flex flex-col md:flex-row gap-6 md:gap-8 mt-4">
          <div className="md:w-[300px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
              <img
                src={comic.cover}
                alt={`${comic.title} - 韩漫漫画网`}
                title={`${comic.title} - 韩漫完整在线观看`}
                className="w-full object-cover"
                width="400"
                height="560"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-foreground-500">
                <i className="ri-user-line mr-1"></i>
                {comic.author}
              </span>
              <span className="text-sm text-foreground-400">|</span>
              <span className="text-sm text-foreground-500">{comic.category}</span>
              <span className="text-sm text-foreground-400">|</span>
              <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                comic.status === "已完结"
                  ? "bg-accent-100 text-accent-700"
                  : "bg-primary-100 text-primary-700"
              }`}>
                {comic.status}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-5">
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-accent-500">{comic.rating}</span>
                <span className="text-xs text-foreground-400">分</span>
              </div>
              <span className="text-sm text-foreground-400">
                <i className="ri-eye-line mr-1"></i>
                {(comic.views / 10000).toFixed(1)}万次阅读
              </span>
              <span className="text-sm text-foreground-400">
                更新于 {comic.updateDate}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-5">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="bg-background-100/70 rounded-lg p-4 mb-6">
              <h2 className="text-sm font-semibold text-foreground-900 mb-2">
                作品简介
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {comic.description}
              </p>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">
                章节列表
              </h2>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <i className="ri-book-read-line text-primary-500 flex-shrink-0"></i>
                      <span className="text-sm text-foreground-800 truncate">
                        {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">
                      {chapter.date}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedComics.length > 0 && (
          <section className="mt-10 md:mt-14">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {relatedComics.map((rc) => (
                <ComicCard key={rc.id} comic={rc} />
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}